import React, { useMemo, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { CompanySpecificProduct } from "@/types";
import { useTranslation } from "react-i18next";
import SettingsTable from "@/components/molecules/SettingsTable/SettingsTable";
import { deleteCompanySpecificProduct, useGetCompanySpecificProducts } from "@/services/Company";
import { useAuth } from "@/contexts/AuthContext";
import Text from "@/components/atoms/Text";
import { SettingsFormContent } from "@/components/pages/Settings";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useModal } from "@/contexts/ModalContext";
import Group from "@/components/atoms/Group";
import CTA from "@/components/atoms/CTA";

interface ProductTableProps {
  setSelectedProduct: React.Dispatch<React.SetStateAction<CompanySpecificProduct | null>>;
}

const ProductTable: React.FC<ProductTableProps> = (props) => {
  const [includeDeleted, setIncludeDeleted] = useState<boolean>(false);
  const { setSelectedProduct } = props;
  const { t } = useTranslation();

  const { currentCompany, isAdminUser } = useAuth();
  const isAdmin = isAdminUser(currentCompany);
  const { data: products } = useGetCompanySpecificProducts(currentCompany?.companyId as number, includeDeleted, {
    enabled: !!currentCompany?.companyId,
  });
  const { openModal, closeModal } = useModal();
  const queryClient = useQueryClient();

  const { mutateAsync: mutateDeleteCompanySpecificProduct } = useMutation({
    mutationFn: async (companySpecificProductId: number) => {
      await deleteCompanySpecificProduct(companySpecificProductId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getCompanySpecificProduct"],
      });
    },
  });

  const columns: ColumnDef<CompanySpecificProduct>[] = useMemo(
    () => [
      {
        header: t("common:name"),
        accessorKey: "companySpecificProductName",
      },
    ],
    [t, products]
  );

  const editActions = (product: CompanySpecificProduct) => [
    {
      label: t("actions:show"),
      action: () => {
        setSelectedProduct(product);
      },
    },
    {
      label: t("settings:deactivate"),
      action: async () =>
        openModal({
          isOpen: true,
          title: t("actions:confirm"),
          content: t("settings:deactivateProductConfirmation"),
          primaryAction: {
            onClick: () => mutateDeleteCompanySpecificProduct(product.id),
            text: t("actions:accept"),
            intent: "secondary",
          },
          secondaryAction: {
            onClick: () => closeModal(),
            text: t("actions:cancel"),
            intent: "primary",
          },
        }),
    },
  ];

  return (
    <SettingsFormContent>
      <Group mb={25} align="center" justify="space-between">
        <Text size="md" strong={true} display="technical" tt="uppercase">
          {t("settings:productList")}
        </Text>
        <CTA intent="secondary" type="button" onClick={() => setIncludeDeleted(!includeDeleted)}>
          {includeDeleted ? t("settings:hideDeactivated") : t("settings:showDeactivated")}
        </CTA>
      </Group>
      {/*TODO => Implement search box*/}
      <SettingsTable data={products || []} columns={columns} editActions={editActions} isAdmin={isAdmin} />
    </SettingsFormContent>
  );
};

export default ProductTable;
